import { useEffect, useState } from 'react'
import {
  type OpalApiV3Component,
  type OpalApiResponseDocument,
} from '@opallabs/api'
import {
  LabelSetOption,
  SwimlaneOption,
  CollectionOption,
  PlanOption,
  BoardOption,
} from './types'
import {
  fetchSwimlaneOptions,
  fetchCollectionOptions,
  fetchPlansData,
  fetchBoardsData,
  processPlanOptions,
  processBoardOptions,
} from './utils'

// Custom hook to read label sets from global Backbone collection
export const useLabelSetsFromBackbone = () => {
  const [labelSets, setLabelSets] = useState<LabelSetOption[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const updateLabelSets = () => {
      try {
        // Access global Backbone collection
        const backboneLabelSets = window.OpalMoments?.Collections?.label_sets

        if (!backboneLabelSets) {
          console.warn('OpalMoments.Collections.label_sets not available')
          setLabelSets([])
          setIsLoading(false)
          return
        }

        // Convert Backbone models to React-friendly format
        const converted = backboneLabelSets.map<LabelSetOption>((labelSet) => {
          const id = labelSet.get('uuid')!
          const name = labelSet.get('name')!

          return {
            value: id,
            label: name,
            labelSet: {
              id,
              name,
            },
          }
        })

        setLabelSets(converted)
        setIsLoading(false)
      } catch (error) {
        console.error('Error reading label sets from Backbone:', error)
        setLabelSets([])
        setIsLoading(false)
      }
    }

    // Initial load
    updateLabelSets()
  }, [])

  return { labelSets, isLoading }
}

export const useSwimlaneOptions = (
  selectedPlan: PlanOption | null,
  workspaceUuid: string | null
) => {
  const [swimlaneOptions, setSwimlaneOptions] = useState<SwimlaneOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedSwimlane, setSelectedSwimlane] =
    useState<SwimlaneOption | null>(null)

  useEffect(() => {
    if (selectedPlan && workspaceUuid) {
      setIsLoading(true)

      fetchSwimlaneOptions(selectedPlan.value, workspaceUuid)
        .then((options) => {
          setSwimlaneOptions(options)
          setIsLoading(false)

          // Auto-select if only one option
          if (options.length === 1) {
            setSelectedSwimlane(options[0])
          }
        })
        .catch((error) => {
          console.error('Error fetching swimlane options:', error)
          setSwimlaneOptions([])
          setIsLoading(false)
        })
    } else {
      setSwimlaneOptions([])
      setSelectedSwimlane(null)
    }
  }, [selectedPlan, workspaceUuid])

  return {
    swimlaneOptions,
    isLoading,
    selectedSwimlane,
    setSelectedSwimlane,
  }
}

export const useCollectionOptions = (selectedBoard: BoardOption | null) => {
  const [collectionOptions, setCollectionOptions] = useState<
    CollectionOption[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedCollection, setSelectedCollection] =
    useState<CollectionOption | null>(null)

  useEffect(() => {
    if (selectedBoard) {
      setIsLoading(true)

      fetchCollectionOptions(selectedBoard.value)
        .then((options) => {
          setCollectionOptions(options)
          setIsLoading(false)

          // Auto-select if only one option
          if (options.length === 1) {
            setSelectedCollection(options[0])
          }
        })
        .catch((error) => {
          console.error('Error fetching collection options:', error)
          setCollectionOptions([])
          setIsLoading(false)
        })
    } else {
      setCollectionOptions([])
      setSelectedCollection(null)
    }
  }, [selectedBoard])

  return {
    collectionOptions,
    isLoading,
    selectedCollection,
    setSelectedCollection,
  }
}

// Custom hook to fetch and manage plans and boards data
export const usePlansAndBoardsData = (
  isOpen: boolean,
  workspaceUuid: string | null,
  storyStartDate: string | null,
  storyEndDate: string | null
) => {
  const [plansData, setPlansData] = useState<
    OpalApiResponseDocument<'ReadBlocksBFFV1', 200>['data']
  >([])
  const [isPlansLoading, setIsPlansLoading] = useState(true)
  const [boardsData, setBoardsData] = useState<OpalApiV3Component<'board'>[]>(
    []
  )
  const [isBoardsLoading, setIsBoardsLoading] = useState(true)

  useEffect(() => {
    if (isOpen && workspaceUuid) {
      // Fetch plans
      setIsPlansLoading(true)
      fetchPlansData(workspaceUuid)
        .then((data) => {
          setPlansData(data)
          setIsPlansLoading(false)
        })
        .catch((error) => {
          console.error('Error fetching plans:', error)
          setIsPlansLoading(false)
        })

      // Fetch boards
      setIsBoardsLoading(true)
      fetchBoardsData(workspaceUuid)
        .then((data) => {
          setBoardsData(data)
          setIsBoardsLoading(false)
        })
        .catch((error) => {
          console.error('Error fetching boards:', error)
          setIsBoardsLoading(false)
        })
    }
  }, [isOpen, workspaceUuid])

  // Process data into options
  const planOptions = processPlanOptions(
    plansData,
    storyStartDate,
    storyEndDate
  )
  const boardOptions = processBoardOptions(boardsData)

  return {
    planOptions,
    boardOptions,
    isPlansLoading,
    isBoardsLoading,
  }
}
